import { Injectable } from '@angular/core';
import { Constants } from '@shared/utils';

import { ToastNotificationService } from '../../services';
import { FileSizePipe } from './file-size.pipe';
import { isImage } from './files';

@Injectable()
export class FileValidationService {
  fileSizePipe = new FileSizePipe();

  constructor(private toastNotificationService: ToastNotificationService) {}

  validateFile(file: File, accept: string, sizeLimit: number): boolean {
    if (!file) {
      return false;
    }

    return (
      this.validateExtension(file.name) &&
      this.validateAccept(file, accept) &&
      this.validateSize(file, sizeLimit) &&
      this.validateName(file) &&
      this.validateFileIsNotEmpty(file)
    );
  }

  validateFiles(
    files: File[],
    accept: string,
    sizeLimit: number,
    imageSizeLimit: number
  ): File[] {
    const validFiles = files.filter((file) =>
      this.validateFile(
        file,
        accept,
        isImage(file) ? imageSizeLimit : sizeLimit
      )
    );

    if (validFiles.length !== files.length) {
      const fileSize = this.fileSizePipe.transform(sizeLimit);
      const imageSize = this.fileSizePipe.transform(imageSizeLimit);

      this.toastNotificationService.error(
        // eslint-disable-next-line max-len
        `Some files could not be uploaded. <br>
        # Allowed file types: ${accept ?? 'any non-executable type'} <br>
        # Empty files are not accepted <br>
        # File size above ${fileSize} is not accepted <br>
        # Image size above ${imageSize} is not accepted <br>
        # File names above 200 characters are not accepted`
      );
    }

    return validFiles;
  }

  //#region Private methods

  private validateAccept(file: File, accept: string): boolean {
    if (!file) {
      return false;
    }

    if (!accept) {
      return true;
    }

    const pattern = accept.replace(/,/g, '|').replace(/\s/g, '');
    const regex = new RegExp(`^.*\.(${pattern})$`, 'gi');

    return regex.test(file.name);
  }

  private validateSize(file: File, sizeLimit: number): boolean {
    if (!file) {
      return false;
    }

    return !sizeLimit || file.size <= sizeLimit;
  }

  private validateName(file: File): boolean {
    if (!file) {
      return false;
    }

    return file.name.length <= 200;
  }

  private validateFileIsNotEmpty(file: File): boolean {
    if (!file) {
      return false;
    }

    return file.size !== 0;
  }

  private validateExtension(fileName: string): boolean {
    const splittedFileName = fileName.split('.');
    const extension = splittedFileName[splittedFileName.length - 1];
    return !Constants.ForbiddenExtensionsList.includes(extension);
  }

  //#endregion
}
